/**
 * MM What's In The Jar — fact-card carousel.
 *
 * The strip is a native scroll-snap container. Arrows nudge it by one card, a mouse can
 * grab and drag it, touch swipes natively, and the dots (mobile) follow whichever card sits
 * at the snap position. The strip's trailing space (CSS) is sized so scrolling stops with
 * the last card centred rather than parked at the left edge; every input shares that limit.
 * On load the second card is parked at the snap position, reproducing the artboard's
 * mid-scroll state with the first card peeking in from the left.
 *
 * Programmatic moves are tweened by hand with snapping paused: Chrome does not reliably
 * complete a smooth `scrollTo` on a mandatory-snap container that also has scroll-padding —
 * it can settle straight back where it started.
 */
const TWEEN_MS = 380;
const REDUCED_MOTION = window.matchMedia?.('(prefers-reduced-motion: reduce)');

class MmWhatsInTheJar extends HTMLElement {
  #listeners = new AbortController();
  #tween = 0;
  #tweenFallback = 0;

  connectedCallback() {
    this.#listeners = new AbortController();
    const { signal } = this.#listeners;

    this.strip = this.querySelector('[data-mm-jar-strip]');
    this.cards = Array.from(this.querySelectorAll('[data-mm-jar-card]'));
    this.dotsWrap = this.querySelector('[data-mm-jar-dots]');
    if (!this.strip || this.cards.length === 0) return;

    this.#buildDots();

    this.querySelector('[data-mm-jar-prev]')?.addEventListener('click', () => this.#step(-1), { signal });
    this.querySelector('[data-mm-jar-next]')?.addEventListener('click', () => this.#step(1), { signal });
    this.strip.addEventListener('scroll', () => this.#syncDots(), { signal, passive: true });
    this.#bindDrag(signal);

    this.#goTo(Math.min(1, this.cards.length - 1), { animate: false });
    this.classList.add('is-ready');
  }

  disconnectedCallback() {
    this.#listeners.abort();
    this.#cancelTween();
  }

  #cancelTween() {
    cancelAnimationFrame(this.#tween);
    clearTimeout(this.#tweenFallback);
    this.strip?.classList.remove('is-moving');
  }

  /** Furthest the strip can scroll: the last card centred (set by the trailing space). */
  get #maxScroll() {
    return this.strip.scrollWidth - this.strip.clientWidth;
  }

  /** Card index nearest the snap position; the end of the strip counts as the last card. */
  get #index() {
    const target = this.strip.scrollLeft;
    if (target >= this.#maxScroll - 1) return this.cards.length - 1;
    let best = 0;
    let bestDistance = Infinity;
    this.cards.forEach((card, i) => {
      const distance = Math.abs(this.#offsetFor(card) - target);
      if (distance < bestDistance) {
        bestDistance = distance;
        best = i;
      }
    });
    return best;
  }

  /** scrollLeft that puts `card` at the strip's scroll-padding start, within the strip's limit. */
  #offsetFor(card) {
    const padding = parseFloat(getComputedStyle(this.strip).scrollPaddingInlineStart) || 0;
    return Math.min(card.offsetLeft - padding, this.#maxScroll);
  }

  /**
   * Walk to the nearest snap position in the requested direction. Positions are the
   * clamped card offsets, so the last one is the strip's end (last card centred) — going
   * back from there lands on the card before it rather than re-resolving to the same spot.
   */
  #step(direction) {
    const current = this.strip.scrollLeft;
    const stops = [...new Set(this.cards.map((card) => Math.round(this.#offsetFor(card))))].sort((a, b) => a - b);
    const target =
      direction > 0 ? stops.find((stop) => stop > current + 1) : [...stops].reverse().find((stop) => stop < current - 1);
    if (target === undefined) return;
    this.#scrollTo(target, true);
    this.#syncDots(this.cards.findIndex((card) => Math.round(this.#offsetFor(card)) === target));
  }

  #goTo(index, { animate }) {
    const card = this.cards[index];
    if (!card) return;
    this.#scrollTo(this.#offsetFor(card), animate);
    this.#syncDots(index);
  }

  /**
   * Move the strip, tweening with snapping paused so the browser cannot pull it back
   * mid-flight. Animation frames stop in hidden tabs, so a timer lands the strip on the
   * target if the tween has not finished by then; reduced-motion users get the jump only.
   */
  #scrollTo(left, animate) {
    this.#cancelTween();
    const strip = this.strip;
    const from = strip.scrollLeft;
    const distance = left - from;

    if (!animate || REDUCED_MOTION?.matches || Math.abs(distance) < 1) {
      strip.scrollLeft = left;
      return;
    }

    strip.classList.add('is-moving');
    const finish = () => {
      this.#cancelTween();
      strip.classList.add('is-moving');
      strip.scrollLeft = left;
      strip.classList.remove('is-moving');
    };
    const start = performance.now();
    const frame = (now) => {
      const t = Math.min(1, (now - start) / TWEEN_MS);
      const eased = 1 - Math.pow(1 - t, 3);
      strip.scrollLeft = from + distance * eased;
      if (t < 1) {
        this.#tween = requestAnimationFrame(frame);
      } else {
        finish();
      }
    };
    this.#tween = requestAnimationFrame(frame);
    this.#tweenFallback = setTimeout(finish, TWEEN_MS + 120);
  }

  /**
   * Mouse grab-and-drag. Snapping is paused for the duration (otherwise every scrollLeft
   * write would snap back), then the nearest card is settled on release. Touch is left to
   * the browser's own scrolling.
   */
  #bindDrag(signal) {
    let startX = 0;
    let startScroll = 0;
    let dragging = false;

    this.strip.addEventListener(
      'pointerdown',
      (event) => {
        if (event.pointerType !== 'mouse' || event.button !== 0) return;
        this.#cancelTween();
        dragging = true;
        startX = event.clientX;
        startScroll = this.strip.scrollLeft;
        this.strip.classList.add('is-dragging');
        this.strip.setPointerCapture(event.pointerId);
      },
      { signal }
    );

    this.strip.addEventListener(
      'pointermove',
      (event) => {
        if (!dragging) return;
        this.strip.scrollLeft = startScroll - (event.clientX - startX);
      },
      { signal }
    );

    const release = (event) => {
      if (!dragging) return;
      dragging = false;
      this.strip.classList.remove('is-dragging');
      if (event.pointerId !== undefined && this.strip.hasPointerCapture(event.pointerId)) {
        this.strip.releasePointerCapture(event.pointerId);
      }
      this.#goTo(this.#index, { animate: true });
    };

    this.strip.addEventListener('pointerup', release, { signal });
    this.strip.addEventListener('pointercancel', release, { signal });
    this.strip.addEventListener('lostpointercapture', release, { signal });
  }

  #buildDots() {
    if (!this.dotsWrap) return;
    this.dotsWrap.replaceChildren(
      ...this.cards.map((_, i) => {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'mm-jar__dot';
        dot.setAttribute('aria-label', `Go to card ${i + 1}`);
        dot.addEventListener('click', () => this.#goTo(i, { animate: true }), { signal: this.#listeners.signal });
        return dot;
      })
    );
    this.dots = Array.from(this.dotsWrap.children);
  }

  #syncDots(index = this.#index) {
    this.dots?.forEach((dot, i) => {
      dot.classList.toggle('is-active', i === index);
      dot.setAttribute('aria-current', i === index ? 'true' : 'false');
    });
  }
}

if (!customElements.get('mm-whats-in-the-jar')) {
  customElements.define('mm-whats-in-the-jar', MmWhatsInTheJar);
}
